// src/ai/providers/DeepSeekProvider.ts
import axios from 'axios'
import { BaseAIProvider, AIRequestPayload, AIResponse, AIConfig, AIProviderModel, AIProviderCallbacks } from '../IAIProvider'

interface DeepSeekResponse {
  id: string
  object: string
  created: number
  model: string
  system_fingerprint: string
  choices: {
    index: number
    delta: {
      content: string
    }
    finish_reason: string
    logprobs: null
  }[]
  usage?: {
    prompt_tokens: number
    completion_tokens: number
    total_tokens: number
    prompt_tokens_details: {
      cached_tokens: number
    }
    prompt_cache_hit_tokens: number
    prompt_cache_miss_tokens: number
  }
}

export class DeepSeekProvider extends BaseAIProvider {
  constructor(config: AIConfig, callbacks?: AIProviderCallbacks) {
    super(config, callbacks)
  }

  async getAvailableModels(): Promise<AIProviderModel[]> {
    try {
      const res = await axios.get(`${this.config.baseUrl}/models`, {
        headers: { 'Authorization': `Bearer ${this.config.apiKey}` }
      })
      return res.data?.data?.map((item: any) => ({
        name: item.id,
        raw: item
      })) || []
    } catch (error) {
      console.error('Failed to get DeepSeek models:', error)
      return []
    }
  }

  async sendRequest(payload: AIRequestPayload, customRequestId?: string): Promise<AIResponse> {
    const requestId = this.getOrGenerateRequestId(customRequestId)
    try {
      const controller = this.recordRequestStart(requestId, payload)
      if (!controller) {
        throw new Error('Failed to create abort controller')
      }
      const res = await axios.post(`${this.config.baseUrl}/chat/completions`, payload, {
        headers: { 'Authorization': `Bearer ${this.config.apiKey}` },
        signal: controller.signal
      })

      const response: AIResponse = {
        text: res.data.choices[0].message?.content || res.data.choices[0].text || '',
        requestId,
        usage: res.data.usage
      }

      this.recordRequestComplete(requestId, response)
      return response
    } catch (error) {
      if (error instanceof Error) {
        this.recordRequestError(requestId, error)
      }
      throw error
    }
  }

  async sendStreamingRequest(payload: AIRequestPayload, onData: (data: AIResponse) => void, customRequestId?: string): Promise<string> {
    const requestId = this.getOrGenerateRequestId(customRequestId)
    try {
      const controller = this.recordRequestStart(requestId, payload)
      if (!controller) {
        throw new Error('Failed to create abort controller')
      }
      const response = await fetch(`${this.config.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.config.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...payload,
          stream: true
        }),
        signal: controller.signal
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('No response body reader available')
      }

      let decoder = new TextDecoder()
      let accumulatedText = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        let chunk = decoder.decode(value).replace(/\n+$/, '').replace(/^data: /, '').split(/\n\ndata:/g)
        chunk.forEach(item => {
          if (/\[DONE\]$/.test(item)) return

          if (item.trim()) {
            const chunkData: DeepSeekResponse | null = JSON.parse(item)
            const content = chunkData?.choices[0]?.delta?.content || ''

            if (content) {
              accumulatedText += content
              onData({
                text: content,
                requestId,
                usage: chunkData?.usage
              })
            }
          }
        })
      }
      // 流式请求完成
      const finalResponse: AIResponse = {
        text: accumulatedText,
        requestId
      }
      this.recordRequestComplete(requestId, finalResponse)
      return accumulatedText
    } catch (error) {
      if (error instanceof Error) {
        this.recordRequestError(requestId, error)
      }
      throw error
    }
  }
}